import { isApiError } from "@clubcore/api";
import { useQuery } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useSyncExternalStore, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { FormatProvider, useT } from "../i18n/I18nProvider";
import { BrandAccent } from "../theme/ThemeProvider";
import { Button } from "../ui/Button";
import { ErrorState, Loading } from "../ui/feedback";
import { useAuth, useMe } from "./auth";
import { clubLogoUrl, displayName } from "./clubLogo";
import type { ClubProfile } from "./club-selection";
import { useApi, useServices } from "./services";

interface ClubValue {
  club: ClubProfile;
  /** Clubs reachable from /auth/me (platform staff reach any club via the directory). */
  memberships: ClubProfile[];
  isPlatformStaff: boolean;
  /** Platform staff pass every permission check on the server; the UI mirrors that. */
  can: (permission: string | undefined) => boolean;
  selectClub: (club: { slug: string }) => void;
  /** Re-reads the club (after its settings changed) so branding, locale, zone and currency follow at once. */
  refreshClub: () => Promise<void>;
  /** Brand name, else the club name; "ClubCore" only when neither is known. */
  brandName: string;
  /** The sidebar-size logo the club uploaded; null shows the ClubCore mark. */
  logoUrl: string | null;
}

const ClubContext = createContext<ClubValue | null>(null);

export const PRODUCT_NAME = "ClubCore";

/** Switches the working club. Works outside ClubGate too (the club-choice page). */
export function useSelectClub(): (club: { slug: string }) => void {
  const { clubSelection, queryClient } = useServices();
  return useCallback(
    (club) => {
      queryClient.clear(); // nothing cached for one club may appear in another
      clubSelection.select(club.slug);
    },
    [clubSelection, queryClient],
  );
}

/**
 * Resolves which club the user is working in and provides it with permissions, formatting and
 * branding. Redirects to /choose-club when a choice is needed. Ordinary users have their clubs in
 * `/auth/me`; platform staff, who have no memberships, read the chosen club from `GET /club`.
 */
export function ClubGate({ children }: { children: ReactNode }) {
  const me = useMe();
  const { refreshMe, signOut } = useAuth();
  const api = useApi();
  const t = useT();
  const { clubSelection } = useServices();
  const selection = useSyncExternalStore(clubSelection.subscribe, clubSelection.getSnapshot);
  const location = useLocation();
  const isPlatformStaff = me.user.is_platform_staff;

  const memberships = me.clubs as ClubProfile[];
  const fromMe = selection.slug ? memberships.find((c) => c.slug === selection.slug) : undefined;
  // People who belong to exactly one club never have to choose it.
  const onlyClub = !selection.slug && !isPlatformStaff && memberships.length === 1 ? memberships[0] : undefined;

  const staffClub = useQuery({
    queryKey: [selection.slug, "club"],
    enabled: isPlatformStaff && Boolean(selection.slug),
    queryFn: ({ signal }) => api.core.currentClub(signal),
    staleTime: 60_000,
  });
  const staffMissing = isPlatformStaff && staffClub.isError && isApiError(staffClub.error) && (staffClub.error.status === 404 || staffClub.error.status === 403);

  useLayoutEffect(() => {
    if (onlyClub) clubSelection.select(onlyClub.slug);
    else if (selection.slug && !isPlatformStaff && !fromMe) clubSelection.forget(); // remembered club no longer available
    else if (staffMissing) clubSelection.forget();
  }, [onlyClub, selection.slug, isPlatformStaff, fromMe, staffMissing, clubSelection]);

  const selectClub = useSelectClub();
  const refreshClub = useCallback(async () => {
    if (isPlatformStaff) await staffClub.refetch();
    else await refreshMe();
  }, [isPlatformStaff, staffClub, refreshMe]);

  const staffData = staffClub.data;
  const club = useMemo<ClubProfile | undefined>(() => (isPlatformStaff ? (staffData ? { ...staffData, viaPlatformStaff: true } : undefined) : fromMe), [isPlatformStaff, staffData, fromMe]);
  const value = useMemo<ClubValue | null>(() => {
    if (!club) return null;
    const granted = new Set(club.permissions);
    return {
      club,
      memberships,
      isPlatformStaff,
      selectClub,
      refreshClub,
      can: (permission) => !permission || isPlatformStaff || granted.has(permission),
      brandName: displayName(club, PRODUCT_NAME),
      logoUrl: clubLogoUrl(club.slug, club.logo_sha256, "small"),
    };
  }, [club, memberships, isPlatformStaff, selectClub, refreshClub]);

  if (!value) {
    if (onlyClub) return null; // selecting; re-renders on the next tick
    if (isPlatformStaff && selection.slug && !staffMissing) {
      if (staffClub.isError) {
        return (
          <div className="cc-center">
            <ErrorState error={staffClub.error} onRetry={() => void staffClub.refetch()} />
            <Button variant="ghost" onClick={() => void signOut()}>
              {t("auth.signOut")}
            </Button>
          </div>
        );
      }
      return (
        <div className="cc-center">
          <Loading />
        </div>
      );
    }
    if (!isPlatformStaff && memberships.length === 0) return <Navigate to="/no-club" replace />;
    return <Navigate to="/choose-club" replace state={{ from: location.pathname + location.search }} />;
  }

  return (
    <ClubContext.Provider value={value}>
      <FormatProvider club={{ locale: value.club.locale, timeZone: value.club.timezone, currency: value.club.currency }}>
        <BrandAccent color={value.club.brand_primary_color} />
        {children}
      </FormatProvider>
    </ClubContext.Provider>
  );
}

export function useClub(): ClubValue {
  const value = useContext(ClubContext);
  if (!value) throw new Error("useClub must be used inside ClubGate");
  return value;
}

export const useCan = () => useClub().can;
